import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Skeleton } from '@mui/material';
import axios from 'axios';
import ShowCard from '../components/ShowCard';
import useRetryOnReconnect from '../components/useRetryOnReconnect';

const isFeatureTile = (index) => index % 6 === 0;

const tileSpanSx = (index) => ({
  gridColumn: { xs: 'span 1', sm: isFeatureTile(index) ? 'span 2' : 'span 1' },
  gridRow: { xs: 'span 1', sm: isFeatureTile(index) ? 'span 2' : 'span 1' },
  // Grid items default to min-width: auto, so a nowrap title can force the track (and the
  // whole grid) wider than the viewport instead of letting its ellipsis/overflow CSS clip it.
  minWidth: 0,
});

// Main component to fetch API data and render multiple cards with layout
export default function ShowList() {
  const [shows, setShows] = useState([]); // State to hold API data
  const [loading, setLoading] = useState(true); // State to track if data is still loading
  const [hasError, setHasError] = useState(false);

  const fetchShows = async () => {
    setLoading(true);
    try {
      // Use the public API endpoint
      const response = await axios.get(`https://broadcast.wssr.stream/api/station/1/public/podcasts`);
      setShows(response.data); // Set the fetched data to state
      setHasError(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShows();
  }, []);

  const showSkeleton = loading || (hasError && shows.length === 0);

  useRetryOnReconnect(fetchShows, hasError);

  return (
    <Box sx={{ flexGrow: 1, p: 2, paddingBottom: '130px' }}> {/* Add paddingBottom to avoid overlap with navigation */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(260px, 1fr))' },
          gridAutoRows: '400px',
          gridAutoFlow: 'dense',
          gap: 2,
        }}
      >
        {showSkeleton
          ? Array.from(new Array(6)).map((_, index) => (
              <Box key={index} sx={tileSpanSx(index)}>
                {/* Skeleton Card */}
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Skeleton variant="rectangular" sx={{ flex: '1 1 auto', minHeight: 160 }} />
                  <CardContent sx={{ flex: '0 0 auto' }}>
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="100%" />
                  </CardContent>
                </Card>
              </Box>
            ))
          : shows.map((show, index) => {
              const { id, title, description, art } = show; // Extract id, title, description, and art

              return (
                <Box key={id} sx={tileSpanSx(index)}>
                  {/* Pass the ID to the ShowCard component for linking */}
                  <ShowCard id={id} title={title} description={description} image={art} />
                </Box>
              );
            })}
      </Box>
    </Box>
  );
}
